<template>
<div>

  <template v-if="isLoadedNormal && hasJobs">
    <p>
      Platforms (amount: {{platformsOrNull.length}})
    <p>
    <ol class="mb-2">
      <li v-for="platform in platformsOrNull" :key="platform">
        {{platform | getPlatformName}}
      </li>
    </ol>

    <p>
      Languages (amount: {{langsOrNull.length}})
    </p>
    <ol class="mb-2">
      <li v-for="lang in langsOrNull" :key="lang">
        {{lang | getLangNameEn}}
      </li>
    </ol>

    <p>
      UTC (amount: {{utcDeltasOrNull.length}})
    </p>
    <ol class="mb-2">
      <li v-for="utcDelta in utcDeltasOrNull" :key="utcDelta">
        {{utcDelta | getSignedNumberStr}}
      </li>
    </ol>

    <p>
      Job amount: {{jobAmount}}
    </p>

    <table class="table">
      <thead class="thead-dark">
        <tr>
          <th>Title</th>
          <th>Message</th>
          <th>Platform</th>
          <th>Lang</th>
          <th>UTC</th>
          <th>Local&nbsp;time</th>
          <th>Total</th>
          <th>Sent</th>
          <th>Fails</th>
          <th>Done</th>
        </tr>
      </thead>
      <tr
        v-for="job in push.jobs"
        :key="job.id"
        :class="getJobClass(job)"
      >
        <td class="job-title">{{job.title}}</td>
        <td class="job-message">{{job.message}}</td>
        <td>{{job.platform | getPlatformName}}</td>
        <td>{{job.language | getLangNameEn}}</td>
        <td class="job-utc">{{job.utc_delta | getSignedNumberStr}}</td>
        <td class="job-local-time">{{getLocalTime(job.utc_delta) | getMomentMinStr}}</td>
        <td class="job-total">{{job.total | getFormattedNumber}}</td>
        <td class="job-sent" >{{job.sent  | getFormattedNumber}}</td>
        <td class="job-fails">{{job.fails | getFormattedNumber}}</td>
        <td class="job-donw">{{job | getJobStateStr}}</td>
      </tr>
    </table>
  </template>

  <p v-if="message !== null" v-html="message">
  </p>

  <loading-indicator
    v-if="isLoading"
  />

</div>
</template>

<script>
import { HTTP } from '../http-common';
import notifier from '../notifier';

import LoadingIndicator from './LoadingIndicator.vue';

import { PUSH_STATUS_INCOMING } from '../model/push';
import { getJobStateStr         } from '../model/push.js';
import { isJobDone              } from '../model/push.js';
import { sortNumbers } from '../util.js';

const LOAD_STATE_INIT          = 1;
const LOAD_STATE_LOADING       = 2;
const LOAD_STATE_LOADED_NORMAL = 3;
const LOAD_STATE_LOADED_ERROR  = 4;

export default {
  name: 'PushJobs',

  components: {
    LoadingIndicator,
  },

  props: {
    pushId: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      loadState: LOAD_STATE_INIT,
      push: null,

      nowSec: Math.floor(Date.now() / 1000),
    };
  },

  computed: {
    isLoading() {
      return this.loadState === LOAD_STATE_LOADING;
    },

    isLoadedNormal() {
      return this.loadState === LOAD_STATE_LOADED_NORMAL;
    },

    message() {
      if(this.isLoadedNormal) {
        if(this.push === null) {
          return `Push whit id ${this.pushId} not found.`;
        }

        if(this.push.status === PUSH_STATUS_INCOMING) {
          return 'Push is incoming.<br>Jobs are not inited yet.';
        }

        if(!this.hasJobs) {
          return 'No jobs';
        }
      }
      return null;
    },

    jobAmount() {
      const jobs = this.jobsOrNull;
      if(jobs === null) {
        return null;
      }

      return jobs.length;
    },

    hasJobs() {
      const jobs = this.push.jobs;
      if(jobs === undefined || jobs === null) {
        return false;
      }
      return jobs.length > 0;
    },

    platformsOrNull() {
      const jobs = this.jobsOrNull;
      if(jobs === null) {
        return null;
      }

      const platforms = [];
      for(const job of jobs) {
        const platform = job.platform;
        if(!platforms.includes(platform)) {
          platforms.push(platform);
        }
      }

      sortNumbers(platforms);
      return platforms;
    },

    langsOrNull() {
      const jobs = this.jobsOrNull;
      if(jobs === null) {
        return null;
      }

      const langs = [];
      for(const job of jobs) {
        const lang = job.language;
        if(!langs.includes(lang)) {
          langs.push(lang);
        }
      }

      sortNumbers(langs);
      return langs;
    },

    utcDeltasOrNull() {
      const jobs = this.jobsOrNull;
      if(jobs === null) {
        return null;
      }

      const utcDeltas = [];
      for(const job of jobs) {
        const utcDelta = job.utc_delta;
        if(!utcDeltas.includes(utcDelta)) {
          utcDeltas.push(utcDelta);
        }
      }

      sortNumbers(utcDeltas);
      return utcDeltas;
    },

    jobsOrNull() {
      const push = this.push;
      if(push === undefined || push === null) {
        return null;
      }

      const jobs = push.jobs;
      if(jobs === undefined || jobs === null) {
        return null;
      }

      return jobs;
    }
  },

  created() {
    this.loadPush();
  },

  methods: {
    loadPush() {
      notifier.deleteSenderNotifications(this);

      this.loadState = LOAD_STATE_LOADING;

      const url = `/push/get/${this.pushId}`;
      HTTP.get(url)
      .then(response => {
        this.push = response.data.data;
        this.loadState = LOAD_STATE_LOADED_NORMAL;
      })
      .catch(e => {
        this.loadState = LOAD_STATE_LOADED_ERROR;
        notifier.showError(e.response.data, {sender: this});
      });
    },

    getLocalTime(utcDelta) {
      const date = new Date();
      const localOffsetSec = date.getTimezoneOffset() * 60;
      const targetOffsetSec = utcDelta * 3600;

      return this.nowSec + localOffsetSec + targetOffsetSec;
    },

    getJobClass(job) {
      if(isJobDone(job)) {
        if(job.fails === 0)
          return 'push-job-success';
        else
          return 'push-job-warning';
      }
      return null;
    },
  },

  filters: {
    getJobStateStr,
  },
}
</script>

<style scoped>
.job-title {
  min-width: 13rem;
}

.job-message {
  min-width: 20rem;
}

.job-utc {
  text-align: right;
}

.job-local-time {
  white-space: nowrap;
}

.job-total,
.job-sent,
.job-fails {
  white-space: nowrap;
  text-align: right;
}

.push-job-success {
  border-color: #8fd19e;
  background-color: #c3e6cb;
}

.push-job-warning {
  border-color: #ffdf7e;
  background-color: #ffeeba;
}
</style>
